import { Badge, Dropdown, MenuProps } from 'antd';
import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { InvoiceStatusMapping, TInvoice, TSupplier } from './type';
import { CSVLink } from 'react-csv';
import { ICCSV } from '../../../icons';
import { useNotification } from '../../../hooks/useNotification';
import { ICMaterialSymbol } from '../../../icons/material-symbol';
import { EInvoiceButton } from '../../../components/button';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { setLoading } from '../../../redux/slices/appInfo';
import { exportInvoices, getAllInvoice } from '../../../api/invoice';
import { TInvoiceRequest } from '../../../api/invoice/type';
import { SearchQueryParams } from '../../../interface/paging';
import { EXTERNAL_CSV_COLUMN } from '../../../utils/constants';
import Papa from 'papaparse';
import { SOURCE_PAGE } from '../../../utils/enums';

const Wrapper = styled.div``;

type ExportActionsProps = {
    source: SOURCE_PAGE;
    selectedRowKeys: React.Key[];
    filterParams: SearchQueryParams;
};

export const ExportActions: React.FC<ExportActionsProps> = ({
    selectedRowKeys,
    filterParams,
    source,
}) => {
    const dispatch = useDispatch();
    const { showSuccess } = useNotification();
    const csvLinkRef = React.useRef<any>(null);
    const [logoBase64, setLogoBase64] = React.useState<string>('');
    const [csvData, setCsvData] = React.useState<any[]>([]);

    // TODO: will be implemented later
    // const convertLogoToBase64 = async (url: string): Promise<string> => {
    //     try {
    //         const response = await fetch(url);
    //         if (!response.ok) {
    //             throw new Error(`HTTP error! status: ${response.status}`);
    //         }

    //         const blob = await response.blob();
    //         return new Promise((resolve, reject) => {
    //             const reader = new FileReader();
    //             reader.onloadend = () => {
    //                 if (typeof reader.result === 'string') {
    //                     resolve(reader.result);
    //                 } else {
    //                     reject(new Error('Failed to convert to base64'));
    //                 }
    //             };
    //             reader.onerror = () => reject(reader.error);
    //             reader.readAsDataURL(blob);
    //         });
    //     } catch (error) {
    //         console.error('Error converting logo to base64:', error);
    //         throw error;
    //     }
    // };

    // React.useEffect(() => {
    //     const loadLogo = async () => {
    //         try {
    //             const base64Logo =
    //                 await convertLogoToBase64('/images/logo.png');
    //             setLogoBase64(base64Logo);
    //         } catch (error) {
    //             console.error('Failed to load logo:', error);
    //         }
    //     };
    //     loadLogo();
    // }, []);

    const handleCSVExport = async () => {
        try {
            dispatch(setLoading(true));
            if (selectedRowKeys.length === 0) {
                const payload: TInvoiceRequest = {
                    ...filterParams,
                    source,
                    pageSize: undefined,
                    created_on: undefined,
                };
                const result = await getAllInvoice(payload);
                if (result.status === 200) {
                    const invoicesResponse = result.data?.data?.invoices ?? [];
                    const supplier = result.data?.data?.supplier ?? {};
                    exportCSVData(invoicesResponse, supplier);
                }
            } else {
                const payload = selectedRowKeys.map(key => key.toString());
                const result = await exportInvoices(payload);
                if (result.status === 200) {
                    const invoicesResponse = result.data?.data?.invoices ?? [];
                    const supplier = result.data?.data?.supplier ?? {};
                    exportCSVData(invoicesResponse, supplier);
                }
            }
        } catch (error) {
            dispatch(setLoading(false));
        }
    };

    const exportCSVData = (result: TInvoice[], supplier: TSupplier) => {
        if (result.length > 0) {
            const flattenInvoices = (invoices: TInvoice[]) => {
                return invoices.flatMap((invoice: TInvoice) => {
                    const invoiceTemp = { ...invoice };
                    if (typeof invoice.buyer_registration_no === 'object') {
                        invoiceTemp['buyer_registration_no_id'] =
                            invoice.buyer_registration_no?.id;
                        invoiceTemp['buyer_registration_no_type'] =
                            invoice.buyer_registration_no?.type;
                    }
                    if (typeof invoice.buyer_address === 'object') {
                        invoiceTemp['buyer_address_line0'] =
                            invoice.buyer_address?.line0;
                        invoiceTemp['buyer_address_line1'] =
                            invoice.buyer_address?.line1;
                        invoiceTemp['buyer_address_line2'] =
                            invoice.buyer_address?.line2;
                        invoiceTemp['buyer_address_postcode'] =
                            invoice.buyer_address?.postal;
                        invoiceTemp['buyer_address_city'] =
                            invoice.buyer_address?.city;
                        invoiceTemp['buyer_address_state'] =
                            invoice.buyer_address?.state?.toString();
                        invoiceTemp['buyer_address_country'] =
                            invoice.buyer_address?.country;
                    }

                    if (typeof supplier === 'object') {
                        invoiceTemp['supplier_name'] = supplier?.name;
                        invoiceTemp['supplier_tin'] = supplier?.tin;
                        if (typeof supplier.registrationNo === 'object') {
                            invoiceTemp['supplier_registrationNo_id'] =
                                supplier.registrationNo?.id;
                            invoiceTemp['supplier_registrationNo_type'] =
                                supplier.registrationNo?.type;
                        }
                        invoiceTemp['supplier_SSTregistrationNo'] =
                            supplier?.SSTregistrationNo;
                        invoiceTemp['supplier_tourismTaxNo'] =
                            supplier?.tourismTaxNo;
                        invoiceTemp['supplier_email'] = supplier?.email;
                        invoiceTemp['supplier_msic'] = supplier?.msic;
                        invoiceTemp['supplier_description'] =
                            supplier?.description;
                        if (typeof supplier.address === 'object') {
                            invoiceTemp['supplier_address_line0'] =
                                supplier.address?.line0;
                            invoiceTemp['supplier_address_line1'] =
                                supplier.address?.line1;
                            invoiceTemp['supplier_address_line2'] =
                                supplier.address?.line2;
                            invoiceTemp['supplier_address_postcode'] =
                                supplier.address?.postal;
                            invoiceTemp['supplier_address_city'] =
                                supplier.address?.city;
                            invoiceTemp['supplier_address_state'] =
                                supplier.address?.state;
                            invoiceTemp['supplier_address_country'] =
                                supplier.address?.country;
                        }
                        invoiceTemp['supplier_phone'] = supplier?.phone;
                    }
                    if (invoice.line_items?.length === 0) {
                        return {
                            ...invoiceTemp,
                        };
                    }
                    const StringValues = [
                        'classification',
                        'description',
                        'taxType',
                        'taxRate',
                    ];
                    const convertLineItem = (lineItem: any) => {
                        const newLineItem: any = {};
                        Object.keys(lineItem).forEach(key => {
                            if (StringValues.includes(key)) {
                                newLineItem[`lineitem_${key}`] =
                                    lineItem[key]?.toString();
                                return;
                            }
                            newLineItem[`lineitem_${key}`] = lineItem[key];
                        });
                        return newLineItem;
                    };
                    return invoice.line_items?.map(lineItem => {
                        return {
                            ...convertLineItem(lineItem),
                            ...invoiceTemp,
                            ...EXTERNAL_CSV_COLUMN,
                        };
                    });
                });
            };
            const newInvoices = flattenInvoices(result);
            const newData = newInvoices?.map(item => {
                delete item?.line_items;
                delete item?.buyer_registration_no;
                delete item?.buyer_address;
                delete item?.id;
                delete item?.tenant_id;
                delete item?.submit_yn;
                delete item?.status;
                delete item?.submite_datetime;
                delete item?.created_on;
                delete item?.updated_on;
                delete item?.count_line_items;
                return item;
            }) as any[];
            const newCsvData = newData.map(item => {
                const newItem = { ...item };
                Object.keys(item).forEach(key => {
                    if (
                        typeof item[key] === 'string' &&
                        !isNaN(Number(item[key]))
                    ) {
                        newItem[key] = `="${item[key]}"`;
                    }
                });
                return newItem;
            });
            const csv = Papa.unparse(newCsvData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `invoices-${dayjs().format('YYYY-MM-DD')}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => {
                dispatch(setLoading(false));
                showSuccess('CSV exported successfully');
            }, 500);
        } else {
            dispatch(setLoading(false));
            showSuccess('No data to export');
        }
    };

    const dataCSVExport = React.useMemo(() => {
        if (!csvData) return [];
        return csvData.map(item => {
            if (item?.created_on) {
                const createdOn = dayjs(item?.created_on, 'YYYY-MM-DD').format(
                    'YYYY-MM-DD'
                );
                return {
                    ...item,
                    created_on: createdOn,
                };
            }
            return {
                ...item,
            };
        });
    }, [csvData]);

    const handleExcelExport = async () => {
        try {
            dispatch(setLoading(true));
            let invoicesExport: TInvoice[] = [];
            if (selectedRowKeys.length === 0) {
                const payload: TInvoiceRequest = {
                    ...filterParams,
                    source,
                    pageSize: undefined,
                    created_on: undefined,
                };
                const result = await getAllInvoice(payload);
                if (result.status === 200) {
                    invoicesExport = result.data?.data?.invoices ?? [];
                }
            } else {
                const payload = selectedRowKeys.map(key => key.toString());
                const result = await exportInvoices(payload);
                if (result.status === 200) {
                    invoicesExport = result.data?.data?.invoices ?? [];
                }
            }
            if (invoicesExport.length > 0) {
                await exportToExcel(invoicesExport);
                showSuccess('Excel exported successfully');
            } else {
                showSuccess('No data to export');
            }
        } catch (error) {
            // Handle error
        } finally {
            dispatch(setLoading(false));
        }
    };

    const exportToExcel = (data: TInvoice[]) => {
        const excelData = data.map(item => ({
            'Folio No.': item.invoice_no,
            'Created Date': dayjs(item.created_on).format('DD/MM/YYYY'),
            Status: InvoiceStatusMapping[item.status],
            'Buyer Name': item.buyer_name,
            'Buyer Tin': item.buyer_tin,
            'Currency Code': item.currency_code,
            'Total Exclude Tax': item.total_exclude_tax,
            'Total Include Tax': item.total_include_tax,
            'Total Payable': item.total_payable,
            'Total Tax': item.total_tax,
            'Total Tax Per Tax Type': item.total_tax_per_tax_type,
            Transaction: item.count_line_items,
        }));

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        const columnWidths = [
            { wch: 15 },
            { wch: 12 },
            { wch: 12 },
            { wch: 30 },
            { wch: 15 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 15 },
            { wch: 20 },
        ];
        ws['!cols'] = columnWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Invoices');

        // Generate Excel file
        XLSX.writeFile(
            wb,
            `invoice-report-${dayjs().format('YYYY-MM-DD-HHmm')}.xlsx`
        );
    };

    const handleExportPDF = async () => {
        try {
            dispatch(setLoading(true));
            let invoicesExport: TInvoice[] = [];
            if (selectedRowKeys.length === 0) {
                const payload: TInvoiceRequest = {
                    ...filterParams,
                    source,
                    pageSize: undefined,
                    created_on: undefined,
                };
                const result = await getAllInvoice(payload);
                if (result.status === 200) {
                    invoicesExport = result.data?.data?.invoices ?? [];
                }
            } else {
                const payload = selectedRowKeys.map(key => key.toString());
                const result = await exportInvoices(payload);
                if (result.status === 200) {
                    invoicesExport = result.data?.data?.invoices ?? [];
                }
            }
            if (invoicesExport.length > 0) {
                await exportToPDF(invoicesExport);
                showSuccess('PDF exported successfully');
            } else {
                showSuccess('No data to export');
            }
        } catch (error) {
            // Handle error
        } finally {
            setTimeout(() => {
                dispatch(setLoading(false));
            }, 500);
        }
    };

    const exportToPDF = async (data: TInvoice[]) => {
        try {
            const doc = new jsPDF('landscape', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            // Initial Y position tracker
            let yPosition = 40;
            const padding = 20;

            // Add logo if available
            if (logoBase64) {
                const imgProps = doc.getImageProperties(logoBase64);
                const imgWidth = 120;
                const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

                doc.addImage(
                    logoBase64,
                    'PNG',
                    40, // x position
                    yPosition,
                    imgWidth,
                    imgHeight,
                    undefined,
                    'FAST'
                );

                // Update Y position after logo
                yPosition += imgHeight + padding;
            }

            // Document title
            doc.setFontSize(20);
            doc.setTextColor(44, 62, 80);
            doc.text('Invoice Report', pageWidth / 2, yPosition, {
                align: 'center',
            });

            // Metadata
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(
                `Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
                40,
                yPosition
            );
            doc.text(`Total Records: ${data.length}`, 40, yPosition + 20);
            yPosition += padding + 10;

            // Define columns
            const columns = [
                { header: 'Folio No.', dataKey: 'invoice_no' },
                { header: 'Created Date', dataKey: 'created_on' },
                { header: 'Status', dataKey: 'status' },
                { header: 'Buyer Name', dataKey: 'buyer_name' },
                { header: 'Buyer Tin', dataKey: 'buyer_tin' },
                { header: 'Currency Code', dataKey: 'currency_code' },
                { header: 'Total Exclude Tax', dataKey: 'total_exclude_tax' },
                { header: 'Total Include Tax', dataKey: 'total_include_tax' },
                { header: 'Total Payable', dataKey: 'total_payable' },
                { header: 'Total Tax', dataKey: 'total_tax' },
                {
                    header: 'Total Tax Per Tax Type',
                    dataKey: 'total_tax_per_tax_type',
                },
                { header: 'Transaction', dataKey: 'count_line_items' },
            ];

            // Format data for PDF
            const tableData = data.map(item => ({
                invoice_no: item?.invoice_no,
                created_on: dayjs(item?.created_on).format('DD/MM/YYYY'),
                status: InvoiceStatusMapping[item.status],
                buyer_name: item.buyer_name,
                buyer_tin: item.buyer_tin,
                currency_code: item.currency_code,
                total_exclude_tax: item.total_exclude_tax,
                total_include_tax: item.total_include_tax,
                total_payable: item.total_payable,
                total_tax: item.total_tax,
                total_tax_per_tax_type: item.total_tax_per_tax_type,
                count_line_items: item.count_line_items,
            }));

            // Generate table
            autoTable(doc, {
                head: [columns.map(col => col.header)],
                body: tableData.map(item =>
                    columns.map(col => item[col.dataKey as keyof typeof item])
                ),
                startY: yPosition,
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                },
                headStyles: {
                    fillColor: [191, 155, 48],
                    textColor: [255, 255, 255],
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'center',
                },
                bodyStyles: {
                    halign: 'left',
                },
                columnStyles: {
                    amount: { halign: 'right' },
                    tax: { halign: 'right' },
                    total: { halign: 'right' },
                },
                alternateRowStyles: {
                    fillColor: [245, 247, 250],
                },
                pageBreak: 'auto',
                showHead: 'everyPage',
                didDrawPage: data => {
                    // Footer
                    doc.setFontSize(8);
                    doc.setTextColor(100);
                    doc.text(
                        `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
                        pageWidth - 40,
                        pageHeight - 30,
                        { align: 'right' }
                    );
                    doc.text(
                        'Generated by E-Invoice System',
                        40,
                        pageHeight - 30
                    );
                },
                margin: { top: 100, right: 40, bottom: 40, left: 40 },
            });
            // Save PDF
            doc.save(`invoice-report-${dayjs().format('YYYY-MM-DD-HHmm')}.pdf`);
        } catch (error) {
            dispatch(setLoading(false));
        }
    };

    const handleClickItems: MenuProps['onClick'] = ({ key }) => {
        setTimeout(() => {
            switch (key) {
                case 'export-csv':
                    handleCSVExport();
                    break;
                case 'export-pdf':
                    handleExportPDF();
                    break;
                case 'export-excel':
                    handleExcelExport();
                    break;
                default:
                    break;
            }
        }, 500);
    };

    const items = [
        {
            key: 'export-csv',
            label: (
                <div className="flex gap-2 items-center">
                    <ICCSV stroke="#000" width={20} height={20} />
                    <span>Export CSV</span>
                </div>
            ),
        },
        {
            key: 'export-pdf',
            label: (
                <div className="flex gap-1 items-center">
                    <ICMaterialSymbol icon="picture_as_pdf" />
                    <span>Export PDF</span>
                </div>
            ),
        },
        {
            key: 'export-excel',
            label: (
                <div className="flex gap-1 items-center">
                    <ICMaterialSymbol icon="table_chart" />
                    <span>Export Excel</span>
                </div>
            ),
        },
    ];

    return (
        <Wrapper className="flex flex-col gap-[20px] box-border">
            <Dropdown
                menu={{
                    items: items,
                    onClick: handleClickItems,
                }}
            >
                <Badge count={selectedRowKeys.length} overflowCount={10}>
                    <EInvoiceButton
                        btnType="outline"
                        style={{
                            width: '150px',
                            height: '40px',
                        }}
                        btnSize="md"
                        onClick={e => e.preventDefault()}
                    >
                        <div className="flex items-center gap-3">
                            Export
                            <ICMaterialSymbol icon="arrow_drop_down" />
                        </div>
                    </EInvoiceButton>
                </Badge>
            </Dropdown>
            <CSVLink
                ref={csvLinkRef}
                data={dataCSVExport}
                filename="invoices.csv"
                style={{ display: 'none' }}
            ></CSVLink>
        </Wrapper>
    );
};
